/*
 * Div2UserFound: This component displays the user information.
 *
 * It receives the entire userInfo returned by GET /user?user=${email}.
 * This component/UI can decide what to display.
 */
import React from "react";
import "./Div2UserInfo.css";
import { useApiActivity } from "../../contexts/ApiActivityContext";

const Div2UserFound = ({
  div2Title,
  userInfo,
  activeStep,
  div2ButtonLabel,
  handleDiv2ButtonPress,
  handleValidateIdButtonPress,
  handleEmailResetButtonPress,
  handleUnlockAccountButtonPress,
  handleAddToMfaPilotButtonPress,
  handleOffNetworkEnrollmentButtonPress,
  offNetworkEnrollmentSuccess,
}) => {
  const { isActive } = useApiActivity();
  if (!userInfo) {
    return null; // Don't render anything if userInfo isn't loaded yet
  }

  if (div2ButtonLabel === "") {
    // Don't render anything if the button label is empty
    return null;
  }

  // If the user is deprovisioned, we need to disable the buttons
  let userDisabled = false;
  if (
    userInfo?.okta?.status === "DEPROVISIONED" ||
    userInfo?.okta?.status === "DISABLED"
  ) {
    userDisabled = true;
    userInfo.okta.status = "DISABLED";
  }

  // If this user has a secondary email set, allow the SD to reset it
  let allowResetSecondaryEmail = false;
  if (userInfo?.okta?.secondEmail?.includes("@")) {
    allowResetSecondaryEmail = true;
  }

  const isPrivilegedUser = userInfo.job?.employeeType === "Privileged User";

  // Handle account expiration time
  const userAccountExpired = userInfo?.activeDirectory?.accountExpired;
  let accountExpirationTimeStr = "";
  // MS uses multiple values for "never" expire
  if (
    userInfo?.activeDirectory?.accountExpires === "30828-09-14T02:48:05.477Z" ||
    userInfo?.activeDirectory?.accountExpires ===
      "9999-12-31T23:59:59.999999+00:00" ||
    userInfo?.activeDirectory?.accountExpires === "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.accountExpires === "1601-01-01T00:00:00.000Z" ||
    userInfo?.activeDirectory?.accountExpires === null
  ) {
    accountExpirationTimeStr = "Never";
  } else {
    const accountExpirationTime = new Date(
      userInfo?.activeDirectory?.accountExpires
    );
    accountExpirationTimeStr = accountExpirationTime.toLocaleString();
  }

  // Handle password expiration time
  const passwordExpired = userInfo?.activeDirectory?.passwordExpired;
  const passwordExpirationTime = new Date(
    userInfo?.activeDirectory?.passwordExpires
  );
  let passwordSetTimeStr = new Date(
    userInfo?.activeDirectory?.passwordLastSet
  ).toLocaleString();
  let passwordExpirationTimeStr = passwordExpirationTime.toLocaleString();

  if (
    userInfo?.activeDirectory?.passwordLastSet ===
      "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.passwordLastSet === null
  ) {
    passwordSetTimeStr = "NEVER";
  }
  if (
    userInfo?.activeDirectory?.passwordExpires ===
      "1601-01-01T00:00:00+00:00" ||
    userInfo?.activeDirectory?.passwordLastSet === null
  ) {
    passwordExpirationTimeStr = "EXPIRED";
  }

  // Handle Locked Out status
  let accountLockedOut = false;
  if (
    userInfo?.activeDirectory?.locked === true ||
    userInfo?.okta?.status === "LOCKED_OUT"
  ) {
    accountLockedOut = true;
    userInfo.okta.status = "LOCKED_OUT";
  }

  // If the user doesn't have any MFA factors, alert the SD user
  let hasMfaFactors = false;
  if (userInfo?.factors) {
    let counter = 0;
    for (const factor of userInfo.factors) {
      if (factor.status === "ACTIVE" && factor.factorType !== "manager") {
        counter++;
      }
    }
    if (counter > 0) {
      hasMfaFactors = true;
    }
  }

  return (
    <div className={`child-div ${activeStep === 2 ? "active-step" : ""}`}>
      <div className="app-div-subtitle">{div2Title}</div>
      <div className="user-info-container app-text">
        <div className="user-info">
          <strong>Name: </strong>
          {userInfo.firstName} {userInfo.lastName}
        </div>
        <div
          className={`user-info ${
            accountLockedOut || userDisabled ? "alert-text" : ""
          }`}
        >
          <strong>Account Status: </strong>
          {userInfo.okta?.status}
        </div>
        <div className="user-info">
          <strong>Type: </strong>
          {userInfo.job?.employeeType}
        </div>
        <div className="user-info">
          <strong>Location: </strong>
          {userInfo.location.city}, {userInfo.location.state},{" "}
          {userInfo.location.countryCode}
        </div>
        <div className="user-info">
          <strong>Building: </strong>
          {userInfo.location.officeLocation}
        </div>
        <div className="user-info">
          <strong>Title: </strong>
          {userInfo.job?.title}
        </div>
        <div className="user-info">
          <strong>Department: </strong>
          {userInfo.job?.department}
        </div>
        <div className="user-info">
          <strong>Email: </strong> {userInfo.email}
        </div>
        <div className="user-info">
          <strong>Last Password Change: </strong>
          {passwordSetTimeStr}
        </div>
        <div className="user-info">
          <strong>Secondary email: </strong>
          {userInfo.okta?.secondEmail}
        </div>
        <div className={`user-info ${passwordExpired ? "alert-text" : ""}`}>
          <strong>
            {passwordExpired ? "Password Expired: " : "Password Expires: "}
          </strong>
          {passwordExpirationTimeStr}
        </div>
        <div className={`user-info ${userAccountExpired ? "alert-text" : ""}`}>
          <strong>
            {userAccountExpired ? "Account Expired: " : "Account Expires: "}
          </strong>
          {accountExpirationTimeStr}
        </div>
      </div>

      <div className="account-alert" hidden={!userDisabled}>
        <div className="alert-title">Account Disabled</div>
        <div className="alert-text">
          The user's manager must follow the instructions in{" "}
          <a
            className="alert-link"
            href="https://niketech.service-now.com/now/nav/ui/classic/params/target/kb_view.do%3Fsysparm_article%3DKB0020736"
            target="_blank"
            rel="noreferrer"
          >
            KB0020736
          </a>{" "}
          to re-enable this account.
        </div>
      </div>

      <div className="account-alert" hidden={!userAccountExpired}>
        <div className="alert-title">Account Expired</div>
        <div className="alert-text">
          The user's manager must follow the instructions in{" "}
          <a
            className="alert-link"
            href="https://niketech.service-now.com/now/nav/ui/classic/params/target/kb_view.do%3Fsysparm_article%3DKB0020736"
            target="_blank"
            rel="noreferrer"
          >
            KB0020736
          </a>{" "}
          to re-enable this account.
        </div>
      </div>

      <div
        className="account-alert"
        hidden={userDisabled || hasMfaFactors || userAccountExpired}
      >
        <div className="alert-title">No MFA enrolled</div>
        <div className="alert-text">
          This user has no MFA factors enrolled.{" "}
          <b>To enroll an MFA factor, you must be on the Nike network.</b> If
          the user is not on the Nike network and cannot use GlobalProtect, see{" "}
          <a
            className="alert-link"
            href="https://niketech.service-now.com/kb_view.do?sysparm_article=KB0020749"
            target="_blank"
            rel="noreferrer"
          >
            KB0020749
          </a>
        </div>
      </div>

      <div
        className="account-alert"
        hidden={!passwordExpired || userDisabled || userAccountExpired}
      >
        <div className="alert-title">Password Change Required</div>
        <div className="alert-text">
          This user must change their password the next time they login.
        </div>
      </div>

      <div
        className="account-alert"
        hidden={!offNetworkEnrollmentSuccess}
        style={{ backgroundColor: "#d4edda", borderColor: "#c3e6cb" }}
      >
        <div className="alert-title" style={{ color: "#155724" }}>
          Off-Network MFA Enrollment Enabled
        </div>
        <div className="alert-text" style={{ color: "#155724" }}>
          This user can now enroll MFA factors from off the Nike network for the next 24 hours.
        </div>
      </div>

      <button
        hidden={!accountLockedOut}
        className={`app-button button-under ${
          isActive ? "button-invalid" : "button-valid"
        }`}
        disabled={isActive}
        onClick={handleUnlockAccountButtonPress}
      >
        Unlock Account
      </button>

      <button
        className={`app-button button-under  ${
          isActive || userDisabled
            ? "button-invalid"
            : "button-valid"
        }`}
        disabled={isActive || userDisabled}
        onClick={handleValidateIdButtonPress}
      >
        Validate Identity
      </button>

      <button
        className={`app-button button-under ${
          isActive || userDisabled
            ? "button-invalid"
            : "button-valid"
        }`}
        disabled={isActive || userDisabled}
        onClick={handleDiv2ButtonPress}
      >
        {div2ButtonLabel}
      </button>

      <button
        hidden={!allowResetSecondaryEmail || isPrivilegedUser}
        className={`app-button button-under ${
          isActive ? "button-invalid" : "button-valid"
        }`}
        disabled={isActive}
        onClick={handleEmailResetButtonPress}
      >
        Reset Secondary Email
      </button>

      <button
        hidden={userDisabled || isPrivilegedUser}
        className={`app-button button-under ${
          isActive ? "button-invalid" : "button-valid"
        }`}
        disabled={isActive}
        onClick={handleAddToMfaPilotButtonPress}
      >
        Add User to MFA-Reset Pilot
      </button>

      <button
        hidden={userDisabled || isPrivilegedUser}
        className={`app-button button-under ${
          isActive ? "button-invalid" : "button-valid"
        }`}
        disabled={isActive}
        onClick={handleOffNetworkEnrollmentButtonPress}
      >
        Allow Off-Network MFA Enrollment
      </button>
    </div>
  );
};

export default Div2UserFound;
